import React, { useEffect, useState } from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import Information from './pages/Information';
import MainNavbar, { areUrlsEqual } from './components/navbar/MainNavbar';
import { makeStyles } from '@material-ui/core';
import CustomGrid from './components/CustomGrid';
import CustomTooltip from './components/localization/Tooltip';
import ReactDOM from 'react-dom';
import CustomAccordion from './components/CustomAccordion';
import { INavigation, NavigationStatus } from './GlobalScript';

const useStyles = makeStyles(() => ({
  root: {}
}));

const replaceElementsWithTooltips = () => {
  const elements = document.querySelectorAll('p.mrs-description-text');
  const parents = new Set<HTMLElement>();

  elements.forEach((element) => {
    const parent = element.parentElement as HTMLElement;
    if (parent && !parents.has(parent)) {
      parents.add(parent);
      // Remove the class from all children with the class
      parent.querySelectorAll('.mrs-description-text').forEach((child) => {
        child.classList.remove('mrs-description-text');
      });
      const text = parent.innerHTML;
      const container = document.createElement('span');
      const previousSibling = parent.previousElementSibling as HTMLElement;
      if (previousSibling) {
        previousSibling.appendChild(container);
        parent.remove(); // Remove the parent element
      } else {
        parent.replaceWith(container);
      }
      ReactDOM.render(<CustomTooltip text={text} />, container);
    }
  });
};

const replaceListsWithCustomGrid = () => {
  const lists = Array.from(document.querySelectorAll('.entity-list-custom .list-id'))
    .map((element) => (element as HTMLInputElement).value)
    .filter(Boolean);

  lists.forEach((id, index) => {
    const rootElement = document.getElementById(`root-${id}`);
    if (rootElement) {
      ReactDOM.render(<CustomGrid key={index} id={id} />, rootElement);
    }
  });
};

const replaceAccordionsWithCustomAccordion = () => {
  const accordions = Array.from(document.querySelectorAll('.accordion-custom'));

  accordions.forEach((accordion, index) => {
    const rootElement = document.getElementById(`root-${accordion.id}`);
    if (rootElement) {
      ReactDOM.render(<CustomAccordion key={index} id={accordion.id} />, rootElement);
    }
  });
};

let hasReplaced = false;
let isRunning = false;
export const replaceAllElements = () => {
  if (isRunning) return;
  isRunning = true;
  replaceElementsWithTooltips();
  replaceListsWithCustomGrid();
  replaceAccordionsWithCustomAccordion();
  if (!hasReplaced) {
    hasReplaced = true;
    observer.observe(document.body, { childList: true, subtree: true });
  }
  isRunning = false;
};
const observer = new MutationObserver(replaceAllElements);

const staticValues = (() => {
  const functionUrl = (document.getElementById('nav_function_url') as HTMLInputElement)?.value || '';
  const langCode = (document.getElementById('lang_code') as HTMLInputElement)?.value || '';
  const candidateId = (document.getElementById('candidate_id') as HTMLInputElement)?.value || '';
  const recommendId = (document.getElementById('recommend_id') as HTMLInputElement)?.value || '';
  const agreementId = (document.getElementById('agreement_id') as HTMLInputElement)?.value || '';
  const medicalHistoryId = (document.getElementById('medical_history_id') as HTMLInputElement)?.value || '';
  const mentalHealthId = (document.getElementById('mental_health_id') as HTMLInputElement)?.value || '';
  const appendIds = `?candidateId=${candidateId}&recommendId=${recommendId}&medicalHistoryId=${medicalHistoryId}&agreementId=${agreementId}&mentalHealthId=${mentalHealthId}`;
  return { functionUrl, langCode, candidateId, appendIds };
})();

interface LayoutProps {
  setLoading: (loading: boolean) => void;
}

const Layout: React.FC<LayoutProps> = ({ setLoading }) => {
  const classes = useStyles();
  const history = useHistory();
  const [initialUrl, setInitialUrl] = useState<string>('');
  const [navigationItems, setNavigationItems] = useState<INavigation[]>([]);

  useEffect(() => {
    const { functionUrl, langCode, candidateId, appendIds } = staticValues;
    const fetchNavigationItems = async () => {
      const getPageStatus = (item: INavigation): NavigationStatus => {
        if (item.children && item.children.length > 0) {
          const childStatuses = item.children.map(getPageStatus);
          const notStartedCount = childStatuses.filter(status => status === NavigationStatus.NotStarted).length;
          const completedCount = childStatuses.filter(status => status === NavigationStatus.Complete).length;

          if (childStatuses.length === notStartedCount) {
            return NavigationStatus.NotStarted;
          } else if (childStatuses.length === completedCount) {
            return NavigationStatus.Complete;
          } else {
            return NavigationStatus.Started;
          }
        }
        return item.status ?? NavigationStatus.NotStarted;
      };
      const errorCallback = (err?: any) => {
        console.error('unable to retrieve sitemap: ' + err);
        setNavigationItems([]);
      }

      const mapINavigationItems = (items?: INavigation[]): INavigation[] => {
        if (!items) return [];
        return items.map(x => {
          const children = mapINavigationItems(x.children);
          return {
            children: children,
            title: x.title,
            url: `/${langCode}/${x.url}${appendIds}`.replace('//', '/'),
            status: getPageStatus({ ...x, children }),
            is_hidden: x.is_hidden,
            open_new_window: x.open_new_window,
          };
        });
      };

      try {
        const response = await fetch(`${functionUrl}&UserId=${candidateId}`, {
          method: 'GET'
        });
        const navigationItems = await response.json() as INavigation[];
        const mappedItems = mapINavigationItems(navigationItems);
        if (mappedItems.length > 0) {
          setInitialUrl(mappedItems[0].url);
          setNavigationItems(mappedItems);
        }
      } catch (e) {
        errorCallback(e);
      }
    };

    fetchNavigationItems();
    replaceAllElements();
  }, []);

  // Add history listener to set loading state on navigation
  useEffect(() => {
    const handleLoading = () => {
      setLoading(true);
    };

    const unlisten = history.listen(handleLoading);

    window.addEventListener('beforeunload', handleLoading);

    return () => {
      unlisten();
      window.removeEventListener('beforeunload', handleLoading);
    };
  }, [history, setLoading]);

  return (
    <>
      <MainNavbar navigationItems={navigationItems} />
      <main id="main-content" className={classes.root}>
        <Switch>
          <Route exact path="/:lang/info" component={Information} />
          <Route exact path="/:lang?" render={() => {
            if (initialUrl) {
              if (!areUrlsEqual(history.location.pathname, initialUrl)) {
                window.location.assign(initialUrl);
              }
            } else {
              if (!(document.getElementById('user_id') as HTMLInputElement)?.value) {
                window.location.assign(history.location.pathname + 'SignIn');
              }
            }
            return null;
          }} />
        </Switch>
      </main>
    </>
  );
};

export default Layout;
